type JsonObject = Record<string, unknown>;

export type SlackConnectionFormState = {
  botToken: string;
  appToken: string;
  userToken: string;
  mode: "socket" | "http";
  signingSecret: string;
  webhookPath: string;
};

const KNOWN_KEYS = new Set([
  "botToken",
  "appToken",
  "userToken",
  "mode",
  "signingSecret",
  "webhookPath",
]);

function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function createDefaultSlackConnectionFormState(): SlackConnectionFormState {
  return {
    botToken: "",
    appToken: "",
    userToken: "",
    mode: "socket",
    signingSecret: "",
    webhookPath: "",
  };
}

export function parseSlackConnectionConfig(
  config: unknown,
): SlackConnectionFormState {
  const base = createDefaultSlackConnectionFormState();
  if (!isRecord(config)) {
    return base;
  }

  const mode = readString(config.mode);

  return {
    botToken: readString(config.botToken),
    appToken: readString(config.appToken),
    userToken: readString(config.userToken),
    mode: mode === "http" ? "http" : "socket",
    signingSecret: readString(config.signingSecret),
    webhookPath: readString(config.webhookPath),
  };
}

export function buildSlackConnectionConfig(
  state: SlackConnectionFormState,
  existingConfig?: unknown,
): JsonObject {
  const preserved: JsonObject = {};
  if (isRecord(existingConfig)) {
    for (const [key, value] of Object.entries(existingConfig)) {
      if (!KNOWN_KEYS.has(key)) {
        if (value !== undefined && value !== null) {
          preserved[key] = value;
        }
      }
    }
  }

  const config: JsonObject = { ...preserved };

  const assignString = (key: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      config[key] = trimmed;
    }
  };

  assignString("botToken", state.botToken);
  assignString("appToken", state.appToken);
  assignString("userToken", state.userToken);
  config.mode = state.mode;
  assignString("signingSecret", state.signingSecret);
  assignString("webhookPath", state.webhookPath);

  return config;
}
